import { EditableTableBasic, Icon, IEditableColumn } from '@aon/aon-tws-common-ui';
import { EditableColumnType } from '@aon/aon-tws-common-ui/build/lib/components/EditableTable';
import * as React from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import Constants from '../../constants';
import ContentConstants from '../../contentConstants';
import { ITranslationEditItem } from '../../models';
import { TranslationHelper } from '../../utilities/TranslationHelper';

/*
	NOTE: Bulk PASTE is handled by modal. 
		  Unsure of behavior if there are multiple instances of the translation editor on the page.
*/

export const defaultColumns: IEditableColumn[] = [
	{ columnKey: 'language', label: 'Language', editable: false },
	{
		columnKey: 'textValue',
		label: 'Translation',
		editable: true,
		placeholder: 'Translation text',
		requiredMessage: ContentConstants.ERROR_MISSING_TRANSLATION,
		type: 'textarea',
		minHeight: '150px',
		maxWidth: '720px',
		maxLength: 150,
	},
	{ columnKey: 'modifiedOn', label: 'Updated', editable: false, dataType: 'date' },
];

export const TranslationEditorContainer = styled.div`
	width: 100%;

	@media (max-width: 795px) {
		overflow-x: scroll;
	}
`;

export const MessageContainer = styled.div`
	width: 100%;

	@media (max-width: 795px) {
		padding: 5px 0;
	}
`;

// editor grid
export const TranslationEditorGridContainer = styled.div<{ unStyledContainer?: boolean }>`
	${(props) =>
		props.unStyledContainer
			? ''
			: `	background-color: #EDF4F7;
			padding: 30px 30px 60px 30px;`}

	width: 100%;

	.tws-editable-table {
		border: 1px solid #999999;
		font-size: 13px;
	}
	.tws-editable-table .tws-editable-head {
		font-weight: 600;
		height: 40px;
	}
	.tws-editable-table .tws-editable-column:nth-child(1) {
		width: 25%;
	}
	.tws-editable-table .tws-editable-column:nth-child(2) {
		border: 2px solid #000000 !important;
		color: #444444;
		padding-bottom: 0;
	}
	.tws-editable-table .tws-editable-column:nth-child(3) {
		width: 15%;
	}
	.tws-editable-table .tws-editable-row .tws-editable-column,
	.tws-editable-table .tws-editable-row .tws-editable-headcolumn {
		border: 1px solid #999999;
		color: #444444;
	}
	.tws-editable-table .tws-editable-row textarea,
	.tws-editable-table .tws-editable-row input {
		color: #444444;
	}
`;
export const TranslationCount = styled.div`
	height: 30px;
`;
export const TranslationCountIcon = styled(Icon)`
	margin-right: 5px;
`;

export interface ITranslationEditorProps {
	enableHtmlEditor: boolean;
	property: string;
	propertyDisplayName: string;
	hasDefaultTranslation: boolean;
	translations: ITranslationEditItem[];
	unStyledContainer?: boolean;
	showErrors?: boolean;
	inputType?: EditableColumnType;
	maxLength?: number;
	onChange: (translations: ITranslationEditItem[]) => void;
}

export class TranslationEditor extends React.Component<ITranslationEditorProps, { columns: IEditableColumn[] }> {
	private editMode = true;

	constructor(props: ITranslationEditorProps) {
		super(props);

		const translationColumn = {
			...defaultColumns[1],
			maxLength: this.props.maxLength ?? 150,
		};

		this.state = { columns: [defaultColumns[0], translationColumn, defaultColumns[2]] };

		this.handleOnChange = this.handleOnChange.bind(this);
		this.handleOnUpdate = this.handleOnUpdate.bind(this);
		this.handleOnPaste = this.handleOnPaste.bind(this);
		this.finishUpdate = this.finishUpdate.bind(this);
	}
	public componentDidMount() {
		const { inputType } = this.props;
		if (inputType === 'input') {
			const columnsForInputType: IEditableColumn[] = defaultColumns.map((c: IEditableColumn) => ({
				...c,
				type: c.type ? inputType : undefined,
			}));
			this.setState({ columns: columnsForInputType });
		}
	}

	public componentDidUpdate() {
		// trick the EditableTable into re-rendering
		this.editMode = true;
	}

	public render() {
		const { enableHtmlEditor, hasDefaultTranslation, property, propertyDisplayName, translations, showErrors } = this.props;
		const { columns } = this.state;

		return (
			<TranslationEditorContainer onPaste={this.handleOnPaste}>
				{hasDefaultTranslation ? (
					<TranslationEditorGridContainer unStyledContainer={this.props.unStyledContainer}>
						<TranslationCount>
							<TranslationCountIcon name={'total'} />
							{translations.length}
						</TranslationCount>
						<EditableTableBasic
							id={'translation-editor-' + property}
							enableHtmlEditor={enableHtmlEditor}
							columns={columns}
							data={translations.map((t: any, index) => ({ ...t, uniqueId: t.uniqueId ?? index }))}
							editMode={this.editMode}
							showErrors={showErrors}
							onChange={this.handleOnChange}
							onUpdate={this.handleOnUpdate}
							htmlEditorMode={'custom'}
							toolbarConfiguration={Constants.customHtmlEditorOptions}
						/>
					</TranslationEditorGridContainer>
				) : (
					<MessageContainer>
						{ContentConstants.TRANSLATIONS_DEFAULT_TRANSLATION_REQUIRED.replace('{property}', propertyDisplayName.toLowerCase())}
					</MessageContainer>
				)}
			</TranslationEditorContainer>
		);
	}

	public handleOnChange(record: ITranslationEditItem) {
		const translations = [...this.props.translations];
		const translationIndex = _.findIndex(translations, (t: ITranslationEditItem) => t.cultureCode === record.cultureCode);
		if (translationIndex > -1) {
			const translationUnderEdit = { ...translations[translationIndex], textValue: record.textValue };
			translations[translationIndex] = translationUnderEdit;
		}
		this.handleOnUpdate(translations);
	}

	public handleOnPaste(e: any) {
		const { hasDefaultTranslation } = this.props;
		const preventPastingOverride = ['INPUT', 'TEXTAREA'];
		if (hasDefaultTranslation && preventPastingOverride.indexOf(e.target.tagName.toLowerCase()) === -1) {
			this.editMode = false;
			const clipboardData = e.clipboardData.getData('text/html');

			const translations = TranslationHelper.mapHTMLToTranslationData(this.props.translations, clipboardData);

			if (translations.length) {
				this.handleOnUpdate(_.orderBy(translations, ['language', 'cultureCode']));
			}
		}
	}

	public handleOnUpdate(translations: ITranslationEditItem[]) {
		const nextTranslations = TranslationHelper.updateTranslationEditState(translations, this.props.translations);
		this.finishUpdate(nextTranslations);
	}

	public finishUpdate(translations: ITranslationEditItem[]) {
		const { onChange } = this.props;

		onChange(translations);
	}
}
